import React, { useState, useEffect, useContext } from 'react';
import { Button, Divider, Typography, makeStyles } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { useSnackbar } from 'notistack';
import { AuthContext } from 'services/auth';
import AudioModal from 'views/admin/MaterialInfo/components/CourseTable/AudioModal';
import PdfModal from 'components/BookingInfo/PdfModal';
import styles from './styles';

const useStyles = makeStyles(styles);

const GroupSessionBookingInfo = ({
  bookings,
  hideOpenButton,
  groupSessionId
}) => {
  const auth = useContext(AuthContext);
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [materialsData, setMaterialsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAudioModalOpen, setIsAudioModalOpen] = useState(false);
  const [currentAudioSrc, setCurrentAudioSrc] = useState(null);
  const [lessonName, setLessonName] = useState(null);
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState(null);

  useEffect(() => {
    const fetchMaterials = async () => {
      try {
        // Step 1: Fetch materials for all bookings
        const materials = await auth.api.materials.getMaterials();

        // Step 2: Fetch courses for each booking's material
        const materialsWithCourses = await Promise.all(
          bookings.map(async (booking) => {
            const materialId = booking.info.topic.material;
            const material = materials.find((m) => m.id === materialId);

            // Fetch the courses for the given material
            const courses = await auth.api.materials.getCourses(materialId);

            // Find the course for the specific topic in the booking
            const course = courses.find(
              (c) => c.id === booking.info.topic.course
            );

            // Find the lesson for the course and the booking
            const lesson = course
              ? course.lessons.find((l) => l.id === booking.info.topic.lesson)
              : null;

            return {
              material,
              course,
              lesson,
              booking
            };
          })
        );

        // Step 3: Set the data to the state
        setMaterialsData(materialsWithCourses);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch materials or courses');
        setLoading(false);
        enqueueSnackbar('Error fetching materials or courses', {
          variant: 'error'
        });
      }
    };

    if (bookings && bookings.length) {
      fetchMaterials();
    }
  }, [bookings, auth, enqueueSnackbar]);

  const handleOpenAudioModal = (audioSrc, name) => {
    setIsAudioModalOpen(true);
    setCurrentAudioSrc(audioSrc);
    setLessonName(name);
  };

  const handleCloseAudioModal = () => {
    setIsAudioModalOpen(false);
    setCurrentAudioSrc(null);
  };

  const handleOpenPdfModal = (pdfUrl, name) => {
    setIsPdfModalOpen(true);
    setCurrentPdfUrl(pdfUrl);
    setLessonName(name);
  };

  const handleClosePdfModal = () => {
    setIsPdfModalOpen(false);
    setCurrentPdfUrl(null);
  };

  const handleOpenMaterial = (lesson, booking) => {
    if (booking.info.topic.lesson === lesson.id) {
      // if (
      //   [
      //     'completed',
      //     'booked',
      //     'in-progress',
      //     'pending-feedback',
      //     'open'
      //   ].includes(booking.status)
      // ) {
      const resource = lesson[`tutorResource`];
      handleOpenPdfModal(null, lesson.name);

      // For students, use the backend endpoint to get signed URLs
      // For tutors, use the direct Firebase Storage access
      if (auth?.role === 'student') {
        auth.api.materials
          .getTutorMaterialUrl(resource, groupSessionId, booking.id)
          .then((pdfUrl) => {
            if (pdfUrl) {
              setCurrentPdfUrl(pdfUrl);
            }
          })
          .catch((error) => {
            enqueueSnackbar(
              error?.message || 'Failed to load material. Please try again.',
              {
                variant: 'error'
              }
            );
          });
      } else {
        // Tutors can access directly
        auth.api.materials
          .getLessonDocument(enqueueSnackbar, resource)
          .then((pdfUrl) => {
            if (pdfUrl) {
              setCurrentPdfUrl(pdfUrl);
            }
          });
      }
      // } else {
      //   enqueueSnackbar(
      //     `You can open the material due to booking status: ${booking.status}.`,
      //     {
      //       variant: 'warning'
      //     }
      //   );
      // }
    } else {
      enqueueSnackbar(
        'This material cannot be opened for the current lesson.',
        {
          variant: 'warning'
        }
      );
    }
  };

  const handleOpenAudio = (lesson, booking) => {
    if (booking.info.topic.lesson === lesson.id) {
      // if (
      //   [
      //     'completed',
      //     'booked',
      //     'in-progress',
      //     'pending-feedback',
      //     'open'
      //   ].includes(booking.status)
      // ) {
      handleOpenAudioModal(lesson?.audioResource, lesson?.name);
      // } else {
      //   enqueueSnackbar(
      //     `You can open the material due to booking status: ${booking.status}.`,
      //     {
      //       variant: 'warning'
      //     }
      //   );
      // }
    } else {
      enqueueSnackbar(
        'This material cannot be opened for the current lesson.',
        {
          variant: 'warning'
        }
      );
    }
  };

  const renderBookingDetails = (materialData) => {
    const { material, course, lesson, booking } = materialData;
    const disabledStatuses = [
      'student-cancelled',
      'tutor-cancelled',
      'tutor-absent',
      'no-feedback',
      'teacher-absent',
      'Teacher Absent',
      'No Feedback',
      'Cancelled'
    ];
    // If any of the key data is missing, return a placeholder
    if (!material || !course || !lesson) {
      return (
        <Typography color="textSecondary" variant="body2">
          Booking information is incomplete. Please try again later.
        </Typography>
      );
    }

    return (
      <div className={classes.details}>
        <div className={classes.detailsHeader}>
          <div className={classes.detailsHeaderImgWrapper}>
            {material.icon ? (
              <img
                className={classes.detailsHeaderImg}
                src={material.icon}
                alt={material.name}
              />
            ) : (
              <Skeleton variant="circle" width={40} height={40} />
            )}
          </div>
          <div className={classes.detailsHeaderInfo}>
            <Typography variant="h5">
              <b>{material.name}</b>
            </Typography>
            <Typography color="textSecondary">
              <b>{course?.name}</b>
            </Typography>
            <Typography color="textSecondary">
              {lesson?.code} {lesson?.name}
            </Typography>
            <Typography color="textSecondary">
              {lesson && `Level ${lesson.level}`}
            </Typography>
            {!hideOpenButton && (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                fullWidth
                // disabled={['student-cancelled', 'tutor-cancelled'].includes(
                //   booking?.status
                // )}
                disabled={disabledStatuses.includes(booking?.status)}
                className={classes.detailsHeaderButton}
                onClick={() => handleOpenMaterial(lesson, booking)}>
                Open Material
              </Button>
            )}
            {!hideOpenButton && lesson?.audioResource && (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                fullWidth
                // disabled={['student-cancelled', 'tutor-cancelled'].includes(
                //   booking?.status
                // )}
                disabled={disabledStatuses.includes(booking?.status)}
                className={classes.detailsHeaderButton}
                onClick={() => handleOpenAudio(lesson, booking)}>
                Open Audio
              </Button>
            )}
          </div>
        </div>
        <Divider className={classes.detailsHeaderDivider} />
      </div>
    );
  };

  if (loading) {
    return (
      <div>
        <Skeleton variant="rectangular" width="100%" height={200} />
        <Skeleton variant="text" width="60%" />
      </div>
    );
  }

  if (error) {
    return <Typography color="error">Error: {error}</Typography>;
  }

  return (
    <div>
      {materialsData
        ?.sort((a, b) =>
          (a.lesson?.code || '').localeCompare(b.lesson?.code || '')
        )
        ?.map((materialData) => (
          <div key={materialData.booking.id}>
            {renderBookingDetails(materialData)}
          </div>
        ))}
      <AudioModal
        open={isAudioModalOpen}
        onClose={handleCloseAudioModal}
        audioPaths={currentAudioSrc}
        auth={auth}
        lessonName={lessonName}
      />
      <PdfModal
        open={isPdfModalOpen}
        onClose={handleClosePdfModal}
        pdfUrl={currentPdfUrl}
        lessonName={lessonName}
      />
    </div>
  );
};

export default GroupSessionBookingInfo;
